import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React, {useEffect, useState} from 'react';
import Markdown from 'react-markdown';

import {CONTACT_EMAIL, contactMailtoUrl} from 'app/components/AgentContactDialog';
import {usePushNotification} from 'app/components/Notification';
import TypewriterStatus from 'app/components/TypewriterStatus';
import {ApiNote} from 'app/modules/Remote/Feature';
import {useUserInfo} from 'app/providers/AuthProvider';
import {recordEvent} from 'app/tools/Analytics';
import {
  AGENT_PROMPT_KEY_IDENTIFY_STRUCTURES,
  LENS_AGENT_USER_ID,
  NOTE_TYPE_AGENT,
  NOTE_TYPE_USER,
  USER_ROLE_REGULAR,
} from 'app/utils/constants';
import * as userUtils from 'app/utils/userUtils';

import AgentExploreNote from '../AgentExploreNote';
import {CONTACT_DIALOG_COPY} from '../contactDialogCopy';
import {RunExemplarVisibility, RunExemplars} from '../fewShotExemplarTypes';
import {
  dismissNotePatch,
  restoreNotePatch,
  saveNotePatch,
  useAgentRunLogs,
  useExploreRunsQuery,
  useMergeExploreNotes,
  useSubmitRunFeedback,
  useUpdateExploreNote,
} from '../hooks';
import {getRunAnalysisPhrases} from '../PromptView/promptConfigs';
import {ApiExploreRun, isRunInFlight} from '../types';
import cs from './RunView.module.scss';

type NoteStatus = 'pending' | 'saved' | 'dismissed';

// Sort order for "sort by status": un-acted-on first, then triaged.
const STATUS_ORDER: Record<NoteStatus, number> = {pending: 0, saved: 1, dismissed: 2};
const STATUS_LABELS: Record<NoteStatus, string> = {
  pending: 'Pending',
  saved: 'Saved',
  dismissed: 'Dismissed',
};
const ALL_STATUSES: NoteStatus[] = ['pending', 'saved', 'dismissed'];

const getNoteStatus = (note: ApiNote): NoteStatus =>
  note.isArchived ? 'dismissed' : note.noteType === NOTE_TYPE_USER ? 'saved' : 'pending';

// A run spawned by a bulk workflow stamps the workflow lineage on its notes
// (properties.workflow_id / run_id are the AgentWorkflow / AgentWorkflowRun ids),
// which is our only handle back to the workflow console from here.
const getWorkflowLineage = (notes: ApiNote[]) => {
  for (const note of notes) {
    const workflowId = note.properties?.workflow_id;
    const workflowRunId = note.properties?.run_id;
    if (workflowId != null && workflowRunId != null) {
      return {workflowId, workflowRunId};
    }
  }
  return null;
};
interface Props {
  featureId: number;
  runId: number;
  focusedNoteId: number | string | null;
  onBack: () => void;
  onActiveRunNotesChange: (notes: ApiNote[], runId: number | null) => void;
  onActiveRunExemplarsChange: (exemplars: RunExemplars | null) => void;
  runExemplarVisibility: RunExemplarVisibility;
  onRunExemplarVisibilityChange: (visibility: RunExemplarVisibility) => void;
  onNoteClick: (note: ApiNote) => void;
  onShowAllObservations: () => void;
  onClearFocus: () => void;
}

const RunView: React.FC<Props> = ({
  featureId,
  runId,
  focusedNoteId,
  onBack,
  onActiveRunNotesChange,
  onActiveRunExemplarsChange,
  runExemplarVisibility,
  onRunExemplarVisibilityChange,
  onNoteClick,
  onShowAllObservations,
  onClearFocus,
}) => {
  const [organization, profile] = useUserInfo();
  const currentUserEmail = profile.get('email');
  const currentUserName = profile.get('name') || currentUserEmail;
  const isUpstreamUser = userUtils.isUpstreamEmail(currentUserEmail);
  const canManageRun = userUtils.atLeastUserRole(USER_ROLE_REGULAR, profile);

  const pushNotification = usePushNotification();

  const hasPolygonalGeometry = (note: ApiNote) =>
    note.geometry?.type === 'Polygon' || note.geometry?.type === 'MultiPolygon';

  const [showLogs, setShowLogs] = useState(false);
  // Which statuses are visible in the list; hide dismissed by default.
  const [visibleStatuses, setVisibleStatuses] = useState<Set<NoteStatus>>(
    () => new Set(['saved', 'pending'])
  );
  const [sortByStatus, setSortByStatus] = useState(false);
  const updateNoteMutation = useUpdateExploreNote(featureId);
  const mergeNotesMutation = useMergeExploreNotes(featureId);
  const {data: runs} = useExploreRunsQuery(featureId); //TODO: pass featureId and pagination
  const run = runs?.find((r) => r.id === runId);
  const isRunInProgress = !!run && isRunInFlight(run.status);
  const {data: logsData, isLoading: isLogsLoading} = useAgentRunLogs(
    runId,
    showLogs && !isRunInProgress
  );

  const runNotes = React.useMemo(() => run?.notes ?? [], [run?.notes]);

  const workflowLineage = getWorkflowLineage(runNotes);

  // Scan runNotes, not displayNotes, so the status filter can't hide the callout.
  const observationLimitReached = runNotes.some(
    (note) => note.properties?.observation_limit_reached
  );
  const limitCopy = CONTACT_DIALOG_COPY['observation-limit'];

  const statusCounts = React.useMemo(() => {
    const counts: Record<NoteStatus, number> = {pending: 0, saved: 0, dismissed: 0};
    runNotes.forEach((note) => counts[getNoteStatus(note)]++);
    return counts;
  }, [runNotes]);

  // The notes actually shown: filtered by visible statuses, optionally reordered
  // by status. Array.sort is stable, so notes keep their original order within a
  // status group.
  const displayNotes = React.useMemo(() => {
    const filtered = runNotes.filter((note) => visibleStatuses.has(getNoteStatus(note)));
    if (!sortByStatus) return filtered;
    return [...filtered].sort(
      (a, b) => STATUS_ORDER[getNoteStatus(a)] - STATUS_ORDER[getNoteStatus(b)]
    );
  }, [runNotes, visibleStatuses, sortByStatus]);

  const isFilterActive = visibleStatuses.size < ALL_STATUSES.length;
  const isSAMAgent = !!(
    run?.config.sam_prompt || run?.config.prompt_key === AGENT_PROMPT_KEY_IDENTIFY_STRUCTURES
  );

  // only allow merge for single-scene Identify Structures runs for now
  const allowMerge = !!run && isSAMAgent && run.scenes.length === 1 && runNotes.length > 1;
  // "Merge into one note" combines every pending note that has an area
  // to contribute to the merged multipolygon.
  const mergeableNotes = React.useMemo(
    () =>
      runNotes.filter((note) => getNoteStatus(note) === 'pending' && hasPolygonalGeometry(note)),
    [runNotes]
  );

  const pendingCount = statusCounts.pending;

  // Only surface the run's observations on the map while the observations list
  // is showing; keep the map in sync with the filtered/sorted list so hidden
  // statuses drop their pins too. Toggling logs on clears them.
  useEffect(() => {
    onActiveRunNotesChange(showLogs ? [] : displayNotes, runId);
  }, [displayNotes, runId, showLogs, onActiveRunNotesChange]);

  useEffect(() => {
    return () => {
      onActiveRunNotesChange([], null);
    };
  }, [onActiveRunNotesChange]);

  const runConfig = run?.config;
  const runExemplars = React.useMemo<RunExemplars | null>(
    () =>
      runConfig?.sam_fewshot_exemplars
        ? {
            positives: runConfig.sam_fewshot_exemplars,
            negatives: runConfig.sam_fewshot_negative_exemplars ?? [],
          }
        : null,
    [runConfig]
  );
  // Shown on the map only alongside the sidebar rows that toggle them.
  const showRunExemplars = !!runExemplars && !isRunInProgress && !showLogs;
  useEffect(() => {
    onActiveRunExemplarsChange(showRunExemplars ? runExemplars : null);
  }, [runExemplars, showRunExemplars, onActiveRunExemplarsChange]);

  useEffect(() => {
    return () => {
      onActiveRunExemplarsChange(null);
    };
  }, [onActiveRunExemplarsChange]);

  const toggleStatus = (status: NoteStatus) => {
    setVisibleStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const handleSaveAll = async () => {
    const toSave = runNotes.filter((note) => getNoteStatus(note) === 'pending');
    if (!toSave.length) {
      return;
    }
    const results = await Promise.allSettled(
      toSave.map((note) =>
        updateNoteMutation.mutateAsync({
          ...note,
          userId: LENS_AGENT_USER_ID,
          noteType: NOTE_TYPE_USER,
          isArchived: false,
        })
      )
    );
    // Summarize after everything settles; pushed last, so it replaces the
    // per-note toasts from the mutation hook.
    const savedCount = results.filter((r) => r.status === 'fulfilled').length;
    if (savedCount < results.length) {
      pushNotification({
        message: savedCount
          ? `Saved ${savedCount} of ${results.length} observations as notes. The rest failed — please try again.`
          : 'There was a problem saving the observations. Please try again.',
        autoHideDuration: 3000,
        options: {intent: B.Intent.DANGER},
      });
    } else {
      pushNotification({
        message: `Saved ${savedCount} ${
          savedCount === 1 ? 'observation' : 'observations'
        } as notes! They're now available in the "Notes" tab.`,
        autoHideDuration: 3000,
        options: {intent: B.Intent.SUCCESS, icon: 'tick-circle'},
      });
    }
  };

  const handleDismissAll = () => {
    const toDismiss = runNotes.filter((note) => getNoteStatus(note) === 'pending');
    // Reset to agent type on dismiss so previously-saved notes stay patchable
    // (mirrors the per-note dismiss), keeping a later restore from 404ing.
    toDismiss.forEach((note) =>
      updateNoteMutation.mutate({...note, isArchived: true, noteType: NOTE_TYPE_AGENT})
    );
    // Un-focus if the focused note was among those dismissed.
    if (toDismiss.some((note) => note.id === focusedNoteId)) {
      onClearFocus();
    }
  };

  const handleMergeAll = () => {
    if (mergeableNotes.length < 2) return;
    mergeNotesMutation.mutate(
      {
        notes: mergeableNotes,
        text:
          run?.config.sam_prompt && run?.config.prompt_key == null
            ? `${mergeableNotes.length} objects (${
                Array.isArray(run.config.sam_prompt)
                  ? run.config.sam_prompt.join(', ')
                  : run.config.sam_prompt
              }) identified in this scene`
            : `${mergeableNotes.length} structures identified in this scene.`,
        // Agent-authored and un-acted-on, so the merged note flows through the
        // same save/dismiss triage as any other observation.
        noteType: NOTE_TYPE_AGENT,
        userId: LENS_AGENT_USER_ID,
        properties: {agent_run_id: runId},
      },
      {
        onSuccess: () => {
          pushNotification({
            message: `Merged ${mergeableNotes.length} observations into one note.`,
            autoHideDuration: 3000,
            options: {intent: B.Intent.SUCCESS, icon: 'tick-circle'},
          });
          recordEvent('Merged observations', {runId});
        },
        onError: () => {
          pushNotification({
            message: 'There was a problem merging the observations. Please try again.',
            autoHideDuration: 3000,
            options: {intent: B.Intent.DANGER},
          });
        },
      }
    );
    // Hide the now-archived sources so the list shows just the merged note.
    setVisibleStatuses(new Set<NoteStatus>(['pending', 'saved']));
    if (mergeableNotes.some((note) => note.id === focusedNoteId)) {
      onClearFocus();
    }
  };

  if (!runs) {
    return (
      <div className={cs.runViewSection}>
        <B.Spinner />
      </div>
    );
  }

  if (!run) {
    return (
      <div className={cs.runViewSection}>
        <B.NonIdealState icon="search" title="Run not found" />
      </div>
    );
  }

  // Dismissed notes don't count as observations: the total is pending + saved,
  // and dismissed cards shown via the status filter don't inflate the displayed
  // count — they get their own "(x dismissed)" suffix instead. The "X of Y"
  // form appears only when the filter hides observations.
  const observationCount = statusCounts.pending + statusCounts.saved;
  const displayedObservationCount = displayNotes.filter(
    (note) => getNoteStatus(note) !== 'dismissed'
  ).length;
  const showDismissedCount =
    !showLogs && visibleStatuses.has('dismissed') && statusCounts.dismissed > 0;

  let observationCountLabel = '';
  if (observationCount || showDismissedCount) {
    observationCountLabel =
      displayedObservationCount < observationCount && !showLogs
        ? `${displayedObservationCount} of ${observationCount} observations`
        : `${observationCount} ${observationCount === 1 ? 'observation' : 'observations'}`;
    if (showDismissedCount) {
      observationCountLabel += ` (${statusCounts.dismissed} dismissed)`;
    }
  }

  if (isRunInProgress && !showLogs) {
    return (
      <div className={cs.runViewSection}>
        <div className={cs.runViewHeader}>
          <B.Button icon="arrow-left" minimal text="Back" onClick={onBack} />
          {isUpstreamUser && (
            <B.Tooltip content="Show logs" placement="left">
              <B.Button icon="console" minimal small onClick={() => setShowLogs(true)} />
            </B.Tooltip>
          )}
        </div>
        <div className={cs.inProgressHero}>
          <B.Spinner size={40} className={cs.inProgressSpinner} />
          <TypewriterStatus phrases={getRunAnalysisPhrases(run)} className={cs.inProgressTitle} />
          <div className={cs.promptPanel}>
            <span className={cs.promptPanelLabel}>Prompt</span>
            <span className={cs.promptPanelText}>{run.config.user_prompt}</span>
          </div>
        </div>
        <div className={cs.inProgressFooter}>
          <div className={cs.inProgressRow}>
            <B.Icon icon="time" />
            <span>Usually takes a few minutes</span>
          </div>
          <div className={cs.inProgressRow}>
            <B.Icon icon="notifications" />
            <span>Safe to navigate away. We'll notify you when observations are ready.</span>
          </div>
          {canManageRun && (
            <B.Button
              fill
              intent="primary"
              className={cs.startAnotherRunButton}
              text="Start another run"
              onClick={onBack}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cs.runViewSection}>
      <div className={cs.runViewHeader}>
        <B.Button icon="arrow-left" minimal text="Back" onClick={onBack} />
        {isUpstreamUser && (
          <B.Tooltip content={showLogs ? 'Hide logs' : 'Show logs'} placement="left">
            <B.Button
              icon="console"
              minimal
              small
              active={showLogs}
              onClick={() => setShowLogs((prev) => !prev)}
            />
          </B.Tooltip>
        )}
      </div>
      <div className={cs.promptDisplay}>
        {run.config.user_prompt}{' '}
        {workflowLineage && (
          <a
            href={`/${organization.get('id')}/agent/workflows/${workflowLineage.workflowId}/runs/${workflowLineage.workflowRunId}`}
            target="_blank  "
            rel="noreferrer"
          >
            <B.Tooltip content="Go to run plan">
              <B.Icon icon="share" size={12} />{' '}
            </B.Tooltip>
          </a>
        )}
      </div>
      {showRunExemplars && (
        <RunExemplarRows
          config={run.config}
          visibility={runExemplarVisibility}
          onVisibilityChange={onRunExemplarVisibilityChange}
        />
      )}

      <div className={cs.runViewHeader}>
        {showLogs ? (
          <span>{observationCountLabel}</span>
        ) : (
          <>
            <button
              type="button"
              className={cs.observationCountButton}
              title="Show all observations"
              onClick={onShowAllObservations}
            >
              {observationCountLabel}
            </button>
            {runNotes.length > 0 && (
              <BulkActionsMenu
                statusCounts={statusCounts}
                mergeCount={mergeableNotes.length}
                isMergePending={mergeNotesMutation.isPending}
                pendingCount={pendingCount}
                visibleStatuses={visibleStatuses}
                sortByStatus={sortByStatus}
                isFilterActive={isFilterActive}
                onSaveAll={handleSaveAll}
                onDismissAll={handleDismissAll}
                onMergeAll={handleMergeAll}
                onToggleStatus={toggleStatus}
                onToggleSort={() => setSortByStatus((prev) => !prev)}
                canManageRun={canManageRun}
                allowMerge={allowMerge}
              />
            )}
          </>
        )}
      </div>
      {showLogs ? (
        <div className={cs.runLogsSection}>
          <div className={cs.logSection}>
            <Markdown>{logsData?.logs ?? run.logs}</Markdown>
            {(isRunInProgress || isLogsLoading) && (
              <div style={{paddingTop: '0.5rem'}}>
                <B.Spinner size={12} />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={cs.runNotesSection}>
          {observationLimitReached && (
            <B.Callout intent="primary" icon="info-sign" title={limitCopy.title}>
              {limitCopy.message} Reach out to us at{' '}
              <a href={contactMailtoUrl(limitCopy.emailSubject, limitCopy.emailBody)}>
                {CONTACT_EMAIL}
              </a>
              .
            </B.Callout>
          )}
          {displayNotes.map((note) => (
            <AgentExploreNote
              key={note.id}
              content={note.text}
              selected={note.id === focusedNoteId}
              dismissed={note.isArchived}
              saved={!note.isArchived && note.noteType === NOTE_TYPE_USER}
              text={note.text}
              authorOptions={[
                {label: 'Agent', value: LENS_AGENT_USER_ID},
                {label: currentUserName, value: currentUserEmail},
              ]}
              defaultAuthorValue={LENS_AGENT_USER_ID}
              onClick={() => onNoteClick(note)}
              onSaveAsNote={(edit) => updateNoteMutation.mutate(saveNotePatch(note, edit))}
              onDismiss={() => {
                updateNoteMutation.mutate(dismissNotePatch(note));
                // Un-focus the note we're dismissing so it isn't left highlighted
                // on the card and the map.
                if (note.id === focusedNoteId) {
                  onClearFocus();
                }
              }}
              onRestore={() => updateNoteMutation.mutate(restoreNotePatch(note))}
              canEdit={canManageRun}
            />
          ))}
          {run.status === 'failed' ? (
            <B.NonIdealState
              icon="error"
              iconMuted={false}
              title="Run failed"
              description={
                <>
                  <div>We're looking into it!</div>
                  <div>This did not count toward your quota</div>
                </>
              }
            />
          ) : runNotes.length === 0 ? (
            <B.NonIdealState
              /**TODO: maybe replace with dummy */
              icon="zoom-out"
              iconMuted={false}
              title="Run complete"
              description="No observations were found"
            />
          ) : displayNotes.length === 0 ? (
            <div className={cs.emptyState}>No observations match the current filter.</div>
          ) : null}
        </div>
      )}
      {run.status === 'completed' && canManageRun && <FeedbackCallout runId={runId} />}
    </div>
  );
};

interface RunExemplarRowsProps {
  config: ApiExploreRun['config'];
  visibility: RunExemplarVisibility;
  onVisibilityChange: (visibility: RunExemplarVisibility) => void;
}

// One map-visibility switch per polarity of the boxes a few-shot run was given.
const RunExemplarRows: React.FC<RunExemplarRowsProps> = ({
  config,
  visibility,
  onVisibilityChange,
}) => {
  const negatives = config.sam_fewshot_negative_exemplars ?? [];
  const searchTerm =
    config.sam_fewshot_search_term ??
    (config.sam_fewshot_ground_refine_with_label ? (config.sam_observation_label ?? '') : '');
  const ignoreTerm = config.sam_fewshot_negative_label ?? '';
  return (
    <div className={cs.exemplarRows}>
      <RunExemplarRow
        title="Examples"
        dotClassName={cs.exemplarDotPositive}
        count={config.sam_fewshot_exemplars?.length ?? 0}
        term={searchTerm}
        checked={visibility.positives}
        onChange={(positives) => onVisibilityChange({...visibility, positives})}
      />
      {(negatives.length > 0 || ignoreTerm.trim()) && (
        <RunExemplarRow
          title="Ignored"
          dotClassName={cs.exemplarDotNegative}
          count={negatives.length}
          term={ignoreTerm}
          checked={visibility.negatives}
          onChange={(negatives) => onVisibilityChange({...visibility, negatives})}
        />
      )}
    </div>
  );
};

interface RunExemplarRowProps {
  title: string;
  dotClassName: string;
  count: number;
  // The text sent to SAM with these boxes; blank when the run used boxes alone.
  term: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const RunExemplarRow: React.FC<RunExemplarRowProps> = ({
  title,
  dotClassName,
  count,
  term,
  checked,
  onChange,
}) => {
  const trimmedTerm = term.trim();
  return (
    <div className={cs.exemplarRow}>
      <span className={classnames(cs.exemplarDot, dotClassName)} />
      <B.Switch
        className={cs.exemplarSwitch}
        label={title}
        checked={checked}
        onChange={(e) => onChange(e.currentTarget.checked)}
      />
      <span className={cs.exemplarMeta}>{count}</span>
      {trimmedTerm && (
        <B.Tag minimal className={cs.exemplarTermTag}>
          {trimmedTerm}
        </B.Tag>
      )}
    </div>
  );
};

const FeedbackCallout: React.FC<{runId: number}> = ({runId}) => {
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const submitFeedback = useSubmitRunFeedback();

  if (submitFeedback.isSuccess) {
    return (
      <div className={cs.feedbackCallout}>
        <span className={cs.feedbackThanks}>Thanks for your feedback!</span>
      </div>
    );
  }

  return (
    <B.Callout>
      <div className={cs.feedbackPrompt}>
        <span className={cs.feedbackLabel}>Was this helpful?</span>
        <B.ButtonGroup>
          <B.Button
            icon="thumbs-up"
            active={helpful === true}
            intent={helpful === true ? 'primary' : 'none'}
            onClick={() => setHelpful((prev) => (prev !== true ? true : null))}
          />
          <B.Button
            icon="thumbs-down"
            active={helpful === false}
            intent={helpful === false ? 'primary' : 'none'}
            onClick={() => setHelpful((prev) => (prev !== false ? false : null))}
          />
        </B.ButtonGroup>
      </div>

      {helpful !== null && (
        <>
          <div className={cs.feedbackCommentRow}>
            <B.TextArea
              className={cs.feedbackCommentInput}
              placeholder="Add a comment (optional)"
              value={comment}
              growVertically
              onChange={(e) => setComment(e.target.value)}
            />
            <B.Button
              text="Send feedback"
              intent="primary"
              fill
              loading={submitFeedback.isPending}
              onClick={() =>
                submitFeedback.mutate({
                  agentRunId: runId,
                  helpful,
                  comment: comment.trim() || undefined,
                })
              }
            />
          </div>
          {submitFeedback.isError && (
            <div className={cs.feedbackError}>
              Something went wrong submitting your feedback. Please try again.
            </div>
          )}
        </>
      )}
    </B.Callout>
  );
};

interface BulkActionsMenuProps {
  statusCounts: Record<NoteStatus, number>;
  allowMerge: boolean;
  mergeCount: number;
  isMergePending: boolean;
  pendingCount: number;
  visibleStatuses: Set<NoteStatus>;
  sortByStatus: boolean;
  isFilterActive: boolean;
  canManageRun: boolean;
  onSaveAll: () => void;
  onDismissAll: () => void;
  onMergeAll: () => void;
  onToggleStatus: (status: NoteStatus) => void;
  onToggleSort: () => void;
}

// Kebab menu next to the observation count: bulk save/dismiss/merge, plus
// status filter and sort toggles.
const BulkActionsMenu: React.FC<BulkActionsMenuProps> = ({
  statusCounts,
  pendingCount,
  allowMerge,
  mergeCount,
  isMergePending,
  visibleStatuses,
  sortByStatus,
  isFilterActive,
  canManageRun,
  onSaveAll,
  onDismissAll,
  onMergeAll,
  onToggleStatus,
  onToggleSort,
}) => (
  <B.Popover
    interactionKind={B.PopoverInteractionKind.CLICK}
    placement="bottom-end"
    content={
      <B.Menu>
        <B.MenuDivider title="Show" />
        {ALL_STATUSES.map((status) => (
          <B.MenuItem
            key={status}
            icon={visibleStatuses.has(status) ? 'tick' : 'blank'}
            text={`${STATUS_LABELS[status]} (${statusCounts[status]})`}
            shouldDismissPopover={false}
            onClick={() => onToggleStatus(status)}
          />
        ))}
        <B.MenuDivider title="Sort" />
        <B.MenuItem
          icon={sortByStatus ? 'tick' : 'blank'}
          text="By status"
          shouldDismissPopover={false}
          onClick={onToggleSort}
        />

        {canManageRun && (
          <>
            <B.MenuDivider
              title={`Pending observation${pendingCount === 1 ? '' : 's'} (${pendingCount})`}
            />
            <B.MenuItem
              text="Save all as notes"
              disabled={pendingCount === 0}
              onClick={() => {
                if (
                  window.confirm(
                    `Save ${pendingCount} pending observation${
                      pendingCount === 1 ? '' : 's'
                    } as notes?\n\nThis will allow them to appear in reports.`
                  )
                ) {
                  onSaveAll();
                }
              }}
            />
            <B.MenuItem
              text="Dismiss all"
              disabled={pendingCount === 0}
              onClick={() => {
                if (
                  window.confirm(
                    `Dismiss ${pendingCount} pending observation${pendingCount === 1 ? '' : 's'}?`
                  )
                ) {
                  onDismissAll();
                }
              }}
            />
            {allowMerge && (
              <B.MenuItem
                text="Merge into one note"
                disabled={mergeCount < 2 || isMergePending}
                onClick={onMergeAll}
              />
            )}
          </>
        )}
      </B.Menu>
    }
  >
    <B.Tooltip content="Note actions">
      <B.AnchorButton icon="more" minimal small active={isFilterActive || sortByStatus} />
    </B.Tooltip>
  </B.Popover>
);

export default RunView;
